import { passwordConfig } from '@unreserved/configs';
import { FILE_TYPE_PRESETS } from '@unreserved/constants/app';

/**
 * Validate email address
 * @param email - Email to validate
 * @returns True if valid email
 */
export function isEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate URL
 * @param url - URL to validate
 * @returns True if valid URL
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a string is empty or only whitespace
 * @param str - String to check
 * @returns True if empty
 */
export function isEmpty(str: string | null | undefined): boolean {
  return !str || str.trim().length === 0;
}

/**
 * Check if value is defined (not null or undefined)
 * @param value - Value to check
 * @returns True if defined
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Validate phone number (basic validation)
 * @param phone - Phone number to validate
 * @returns True if valid phone number
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  const digitsOnly = phone.replace(/\D/g, '');
  return (
    phoneRegex.test(phone) && digitsOnly.length >= 10 && digitsOnly.length <= 15
  );
}

/**
 * Extract file extension from URL, handling query parameters and fragments
 * @param url - URL to extract extension from
 * @returns File extension (e.g., ".jpg", ".png") or empty string if not found
 */
export function getFileExtensionFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    const match = pathname.match(/\.([a-z0-9]+)$/i);
    return match ? `.${match[1]}` : '';
  } catch {
    const match = url.toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/i);
    return match ? `.${match[1]}` : '';
  }
}

/**
 * Check if URL is a valid image URL, handling query parameters and metadata
 * @param url - URL to validate
 * @param allowedExtensions - Array of allowed image extensions (e.g., [".jpg", ".png"])
 * @returns True if URL is a valid image URL
 */
export function isValidImageUrl(
  url: string,
  allowedExtensions: readonly string[] = FILE_TYPE_PRESETS.images.extensions,
): boolean {
  if (!isValidUrl(url)) {
    return false;
  }

  const extension = getFileExtensionFromUrl(url);
  return allowedExtensions.some((ext) => extension === ext.toLowerCase());
}

/**
 * Check if URL is a valid document URL, handling query parameters and metadata
 * @param url - URL to validate
 * @param allowedExtensions - Array of allowed document extensions (e.g., [".pdf"])
 * @returns True if URL is a valid document URL
 */
export function isValidDocumentUrl(
  url: string,
  allowedExtensions: readonly string[] = FILE_TYPE_PRESETS.documents.extensions,
): boolean {
  if (!isValidUrl(url)) {
    return false;
  }

  const extension = getFileExtensionFromUrl(url);
  return allowedExtensions.some((ext) => extension === ext.toLowerCase());
}
export interface PasswordValidationRule {
  isValid: boolean;
  message: string;
}

export const EXCLUDED_PASSWORD_SCHEMA_MESSAGES = [
  'Password must be at least 8 characters',
  'Password must contain at least 1 uppercase letter',
  'Password must contain at least 1 special character',
] as const;

export function getPasswordValidationRules(
  password: string,
): PasswordValidationRule[] {
  return [
    {
      isValid: password.length >= 8,
      message: 'Must be at least 8 characters',
    },
    {
      isValid: passwordConfig.uppercaseRegex.test(password),
      message: 'Must be having 1 uppercase',
    },
    {
      isValid: passwordConfig.specialCharRegex.test(password),
      message: 'Must be having 1 special character',
    },
  ];
}
